import React from 'react';
import clsx from 'clsx';
import SanityImage from 'components/SanityImage';
import {Link} from 'components/Link';
import type {SidebarItem} from 'domains/global';
import {useLocation, useMatches} from '@remix-run/react';
import {withHongKongDialCode} from '~/lib/utils';
import MarselloPortalDrawer from './MarselloPortalDrawer';

// Every CTA shares one shape — a WHITE disc — per the approved design
// ("用 widget 後面的白色背景").
// Explicit heights matching the sidebar column width (w-8 / md:w-[50px]) —
// `aspect-square` can't be used here because the legacy
// @tailwindcss/aspect-ratio plugin overrides the native aspect utilities,
// which let the buttons collapse into ovals.
const ICON_BUTTON_BASE =
  'flex h-8 w-full items-center justify-center rounded-full bg-white border border-solid shadow-md transition-opacity duration-200 hover:opacity-80 md:h-[50px]';

/** Contact CTAs and 會員專區 — brand orange. */
export const ICON_BUTTON = `${ICON_BUTTON_BASE} text-hk-orange border-hk-orange/20`;
/**
 * 限時優惠 — the same red the 特賣場 menu item uses (MenuBarDesktop), so the
 * store's two sale surfaces read as one thing rather than two unrelated promos.
 */
const ICON_BUTTON_SALE = `${ICON_BUTTON_BASE} text-hk-red border-hk-red/20`;

/** Smart collection matching every product tagged `flash`. */
const FLASH_SALE_URL = '/collections/flash-sale';

/**
 * 限時優惠 — pinned to the top of the CTA stack, above the contact buttons.
 * A percent sign rather than a wordmark so it reads at 16px on mobile, styled
 * like the 會員專區 entry below: icon disc, then a small label.
 */
function FlashSaleCta() {
  return (
    <Link
      to={FLASH_SALE_URL}
      title="限時優惠"
      aria-label="限時優惠"
      className="group flex w-full flex-col items-center gap-1 outline-none"
    >
      <span className={ICON_BUTTON_SALE}>
        <svg
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
          className="h-4 w-4 md:h-6 md:w-6"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M18.78 5.22a1.25 1.25 0 010 1.77L6.99 18.78a1.25 1.25 0 11-1.77-1.77L17.01 5.22a1.25 1.25 0 011.77 0zM7 3.5a3.5 3.5 0 100 7 3.5 3.5 0 000-7zm0 2a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm10 8a3.5 3.5 0 100 7 3.5 3.5 0 000-7zm0 2a1.5 1.5 0 100 3 1.5 1.5 0 000-3z"
          />
        </svg>
      </span>
      <span className="text-center text-[10px] font-semibold leading-tight text-hk-red md:text-xs">
        限時優惠
      </span>
    </Link>
  );
}

type IconKey = 'top' | 'phone' | 'whatsapp' | 'wechat' | 'location';

/**
 * Map a Sanity sidebar item to one of the shared icons. Matches on the
 * item's `type` first, then falls back to the URL scheme / title, so it keeps
 * working even if an editor labels the type differently. Returns null for
 * anything unrecognised, in which case the original Sanity icon is used.
 */
function resolveIcon(item: SidebarItem): IconKey | null {
  const type = (item.type || '').toLowerCase();
  const url = (item.url || '').toLowerCase();
  const title = (item.title || '').toLowerCase();
  const has = (hay: string, ...needles: string[]) =>
    needles.some((n) => hay.includes(n));

  if (item.type === 'back-to-top' || has(type, 'top')) return 'top';
  if (has(type, 'whatsapp') || has(url, 'wa.me', 'whatsapp')) return 'whatsapp';
  if (has(type, 'wechat', 'weixin') || has(url, 'wechat', 'weixin'))
    return 'wechat';
  if (has(type, 'phone', 'call', 'tel') || url.startsWith('tel:'))
    return 'phone';
  if (
    has(type, 'location', 'map', 'store', 'address') ||
    has(url, 'maps', 'goo.gl') ||
    has(title, 'location', 'map')
  )
    return 'location';
  return null;
}

const ICON_PATHS: Record<IconKey, React.ReactNode> = {
  top: <path d="M12 4l7 7h-4v9h-6v-9H5z" />,
  phone: (
    <path d="M6.62 10.79a15.53 15.53 0 006.59 6.59l2.2-2.2a1 1 0 011.02-.24 11.36 11.36 0 003.57.57 1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.45.57 3.57a1 1 0 01-.24 1.02l-2.2 2.2z" />
  ),
  // Official WhatsApp / WeChat glyphs (Simple Icons paths) so the buttons
  // read instantly as those apps, not generic chat bubbles.
  whatsapp: (
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  ),
  wechat: (
    <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 01.213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.132.295.295.295a.326.326 0 00.167-.054l1.903-1.114a.864.864 0 01.717-.098 10.16 10.16 0 002.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 01-1.162 1.178A1.17 1.17 0 014.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 01-1.162 1.178 1.17 1.17 0 01-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 01.598.082l1.584.926a.272.272 0 00.14.047c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 01-.023-.156.49.49 0 01.201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.89c-.135-.01-.27-.027-.407-.03zm-2.53 3.274c.535 0 .969.44.969.982a.976.976 0 01-.969.983.976.976 0 01-.969-.983c0-.542.434-.982.97-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 01-.968.983.976.976 0 01-.969-.983c0-.542.433-.982.968-.982z" />
  ),
  location: (
    <path d="M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5A2.5 2.5 0 1112 6.5a2.5 2.5 0 010 5z" />
  ),
};

function CtaIcon({name}: {name: IconKey}) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className="h-4 w-4 md:h-6 md:w-6"
    >
      {ICON_PATHS[name]}
    </svg>
  );
}

export default function Sidebar() {
  const [root] = useMatches();
  const location = useLocation();
  const sidebarItems = root.data.sidebar as SidebarItem[];

  // Fade the floating CTA out once the page's dark bottom sections (迎新優惠
  // EmailSignUp + Footer, both navy) scroll into view, so the stack never
  // sits over them — which showed a dark block behind the white icon discs.
  // Those sections carry the same contact details, so hiding loses nothing.
  const [overDarkFooter, setOverDarkFooter] = React.useState(false);
  React.useEffect(() => {
    const targets = [
      document.getElementById('email-signup'),
      document.getElementById('footer'),
    ].filter((el): el is HTMLElement => Boolean(el));
    if (targets.length === 0) return;
    const visible = new Set<Element>();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) visible.add(e.target);
        else visible.delete(e.target);
      });
      setOverDarkFooter(visible.size > 0);
    });
    targets.forEach((t) => io.observe(t));
    return () => io.disconnect();
  }, []);

  return (
    // Anchored to the BOTTOM on md+ (was vertically centred with top-1/2):
    // centring pushed the tall stack up into the fixed header whenever the
    // viewport got short (browser zoom >= 100%). Bottom-anchoring can never
    // collide with the header. Mobile keeps its existing position.
    <div
      className={clsx(
        'fixed right-0 top-auto bottom-0 z-10 -translate-y-3/4 -translate-x-4 transition-opacity duration-300 md:-translate-x-6 md:translate-y-0 md:bottom-6',
        overDarkFooter ? 'pointer-events-none opacity-0' : 'opacity-100',
      )}
    >
      <div className="grid w-8 grid-cols-1 gap-3 md:w-[50px] md:gap-5">
        <FlashSaleCta />
        {sidebarItems &&
          sidebarItems.map((item) => {
            const icon = resolveIcon(item);
            // Fall back to the CMS icon for any unrecognised item so nothing
            // ever disappears from the stack.
            const visual = icon ? (
              <CtaIcon name={icon} />
            ) : (
              <SanityImage
                layout="responsive"
                sizes={['50vw, 100vw']}
                src={item.icon?.asset._ref}
                alt=""
                loading="lazy"
                className="h-full object-cover"
              />
            );

            if (item.type === 'back-to-top') {
              return (
                <button
                  key={item._key}
                  title={item.title}
                  aria-label={item.title}
                  className={icon ? ICON_BUTTON : ''}
                  onClick={() => {
                    typeof window !== 'undefined' &&
                      window?.scrollTo({
                        top: 0,
                        behavior: 'smooth',
                      });
                  }}
                >
                  {visual}
                </button>
              );
            }

            // Built from the router location, not window.location, which
            // the server renders as 'false'. React keeps the server's
            // attributes when hydration succeeds, so the message would read
            // '我正瀏覽這裏: false'.
            const whatsappMessage = `?text=${encodeURIComponent(
              `我正瀏覽這裏: https://www.helmetking.com${location.pathname}`,
            )}`;
            return (
              <a
                key={item._key}
                href={`${withHongKongDialCode(item.url)}${
                  item.type === 'whatsapp' ? whatsappMessage : ''
                }`}
                target="_blank"
                title={item.title}
                aria-label={item.title}
                rel="noreferrer noopener"
                className={icon ? ICON_BUTTON : ''}
              >
                {visual}
              </a>
            );
          })}
        <MarselloPortalDrawer />
      </div>
    </div>
  );
}
